import type { SearchResponse } from '@jmc/shared';
import { formatCount, formatDate } from '@jmc/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { ErrorState, LoadingBlock, Mono } from '../components/ui.js';

/**
 * A printable list to carry to the record room.
 *
 * Until now the only print button was on a single file's page, so fetching
 * twelve files meant opening and printing twelve pages. This takes the same
 * search the clerk already ran and lays it out for paper: sorted by shelf so
 * the walk is one pass through the room, with a tick box per row.
 *
 * It takes the same query parameters as the search screen, so "print this
 * result" is a link rather than a second way of asking the question.
 */
export function PickListPage() {
  const { t, pick } = useI18n();
  const [params] = useSearchParams();

  const query = new URLSearchParams(params);
  query.set('limit', '100');
  query.set('sort', 'fileNumber');

  const result = useQuery({
    queryKey: ['picklist', query.toString()],
    queryFn: () => api<SearchResponse>(`/files?${query.toString()}`),
  });

  // Open the print dialog once the rows are on screen. A clerk reaches this
  // page by pressing Print; making them press it again is just friction.
  useEffect(() => {
    if (result.isSuccess && result.data.items.length > 0) {
      const timer = setTimeout(() => window.print(), 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [result.isSuccess, result.data?.items.length]);

  if (result.isLoading) return <LoadingBlock />;
  if (result.isError) {
    return <ErrorState error={result.error} onRetry={() => void result.refetch()} />;
  }

  const items = [...(result.data?.items ?? [])].sort((a, b) => {
    const key = (file: typeof a) =>
      file.location
        ? `${file.location.roomCode}/${file.location.rackCode}/${file.location.columnCode}/${file.location.bagCode}`
        : 'zzz';
    return key(a).localeCompare(key(b), undefined, { numeric: true });
  });

  return (
    <div className="mx-auto max-w-4xl">
      <div className="no-print mb-4 flex items-center justify-between gap-3">
        <button type="button" className="btn-secondary" onClick={() => window.history.back()}>
          {t('action.back')}
        </button>
        <button type="button" className="btn-primary" onClick={() => window.print()}>
          {t('action.print')}
        </button>
      </div>

      <header className="mb-4 border-b border-line pb-3">
        <h1 className="text-lg font-semibold">{t('picklist.title')}</h1>
        <p className="text-[0.8125rem] text-ink-500">
          {t('app.org')} · {formatDate(new Date())} · {formatCount(items.length)}{' '}
          {t('locations.files')}
          {(result.data?.totalCount ?? 0) > items.length && (
            <span className="ml-2 text-amber-ink">
              {t('picklist.truncated', { shown: items.length, total: result.data!.totalCount! })}
            </span>
          )}
        </p>
      </header>

      {items.length === 0 ? (
        <p className="py-10 text-center text-ink-500">{t('search.noResults')}</p>
      ) : (
        <table className="data-table text-[0.8125rem]">
          <thead>
            <tr>
              <th className="w-8" />
              <th>{t('field.location')}</th>
              <th>{t('field.eFileNumber')}</th>
              <th>{t('field.allottee')} / {t('field.subject')}</th>
              <th>{t('field.status')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((file) => (
              <tr key={file.id}>
                {/* A box to tick as each file is pulled off the shelf. */}
                <td>
                  <span className="inline-block h-3.5 w-3.5 border border-ink-500" />
                </td>
                <td className="whitespace-nowrap">
                  {file.location ? (
                    <Mono className="font-medium">
                      {file.location.roomCode} / {file.location.rackCode} /{' '}
                      {file.location.columnCode} / {file.location.bagCode}
                    </Mono>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <Mono>{file.eFileNumber}</Mono>
                </td>
                <td className="max-w-[18rem] truncate">
                  {pick(file.firstAllotteeEn, file.firstAllotteeHi) ||
                    pick(file.subjectEn, file.subjectHi) ||
                    '—'}
                </td>
                <td>{t(`status.${file.status}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="mt-6 flex justify-between border-t border-line pt-3 text-[0.75rem] text-ink-500">
        <span>{t('picklist.collectedBy')} ____________________</span>
        <span>{t('picklist.date')} ____________________</span>
      </footer>
    </div>
  );
}
